import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAdminAuth } from "./AdminAuthContext";
import { useToast } from "./ToastProvider";
import { StatusPill, formatINR, fmtDate } from "./widgets";
import { X, Search } from "lucide-react";

const STATUSES = ["all", "pending", "processing", "shipped", "delivered", "cancelled"];

export default function AdminOrders() {
  const { apiFetch } = useAdminAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState(params.get("status") || "all");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(params.get("id") || null);
  const [order, setOrder] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [shipping, setShipping] = useState(false);
  const [labelLoading, setLabelLoading] = useState(false);

  const reload = useCallback(() => {
    const url = `/admin/orders?status_filter=${filter}${q ? `&q=${encodeURIComponent(q)}` : ""}`;
    apiFetch(url).then(({ data }) => setItems(data.items || [])).catch(() => {});
  }, [filter, q, apiFetch]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (!openId) { setOrder(null); return; }
    apiFetch(`/admin/orders/${openId}`).then(({ data }) => {
      setOrder(data);
      setTrackingNumber(data?.tracking_number || "");
    }).catch(() => setOrder(null));
  }, [openId, apiFetch]);

  // Escape closes the drawer
  useEffect(() => {
    if (!order) return;
    const onKey = (e) => {
      if (e.key === "Escape") { setOpenId(null); setParams({}); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [order, setParams]);

  const advance = async (newStatus) => {
    if (!order) return;
    try {
      const res = await apiFetch(`/admin/orders/${order.id}/status`, {
        method: "PATCH",
        data: { status: newStatus, tracking_number: newStatus === "shipped" ? trackingNumber : null },
        headers: { "Content-Type": "application/json" },
      });
      setOrder(res.data.order);
      toast(`Order ${newStatus}`);
      reload();
    } catch (e) {
      toast("Update failed", "err");
    }
  };

  const fetchLabel = async () => {
    if (!order) return;
    setLabelLoading(true);
    try {
      const res = await apiFetch(`/admin/orders/${order.id}/label`, {
        method: "POST",
      });
      const url = res.data?.label_url;
      if (url) {
        setOrder({ ...order, shipping_meta: { ...(order.shipping_meta || {}), label_url: url } });
        window.open(url, "_blank", "noopener,noreferrer");
        toast("Label opened in new tab");
      } else {
        toast("Label not ready yet — try again after pickup is scheduled", "err");
      }
    } catch (e) {
      toast(e?.response?.data?.detail || "Label fetch failed", "err");
    } finally {
      setLabelLoading(false);
    }
  };

  const shipViaShiprocket = async () => {
    if (!order) return;
    if (!window.confirm(`Push order ${order.id} to Shiprocket, assign AWB & schedule pickup?`)) return;
    setShipping(true);
    try {
      const res = await apiFetch(`/admin/orders/${order.id}/ship`, {
        method: "POST",
        data: { schedule_pickup_now: true },
        headers: { "Content-Type": "application/json" },
      });
      // Refresh the order to get the latest status + shipping_meta
      const fresh = await apiFetch(`/admin/orders/${order.id}`);
      setOrder(fresh.data);
      const sm = res.data?.shipping_meta || {};
      toast(`Shipped via ${sm.courier_name || "Shiprocket"} - AWB ${sm.awb || "pending"}`);
      reload();
    } catch (e) {
      toast(e?.response?.data?.detail || "Shiprocket call failed", "err");
    } finally {
      setShipping(false);
    }
  };

  const counts = {
    all: items.length,
    pending: items.filter((i) => i.status === "pending").length,
    processing: items.filter((i) => i.status === "processing").length,
    shipped: items.filter((i) => i.status === "shipped").length,
    delivered: items.filter((i) => i.status === "delivered").length,
    cancelled: items.filter((i) => i.status === "cancelled").length,
  };

  return (
    <>
      <div className="adm-page-head">
        <div>
          <h1 className="adm-h1">Orders</h1>
          <div className="adm-h1-sub">{items.length} {items.length === 1 ? "order" : "orders"}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div className="adm-search" style={{ width: 280 }}>
            <Search size={14} />
            <input
              className="adm-input"
              style={{ background: "transparent", border: 0, padding: 0, fontSize: 13 }}
              placeholder="Search id, name, email..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="adm-tabs">
        {STATUSES.map((s) => (
          <button key={s} className={`adm-tab ${filter === s ? "active" : ""}`} onClick={() => { setFilter(s); setParams(s === "all" ? {} : { status: s }); }}>
            {s} <span className="count">{counts[s] || 0}</span>
          </button>
        ))}
      </div>

      <div className="adm-card" style={{ padding: 0 }}>
        <table className="adm-table">
          <thead>
            <tr>
              <th>Order</th><th>Customer</th><th>Items</th><th>Payment</th><th>Status</th><th>Date</th><th style={{ textAlign: "right" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={7} className="adm-empty">No orders match.</td></tr> :
              items.map((o) => (
                <tr key={o.id} className="clickable" onClick={() => setOpenId(o.id)}>
                  <td className="adm-mono">{o.id}</td>
                  <td>{o.customer_name}<div className="adm-cell-name-sub">{o.customer_email}</div></td>
                  <td className="adm-mono" style={{ color: "#8B8B93" }}>{(o.line_items || []).reduce((s, x) => s + x.qty, 0)}</td>
                  <td><StatusPill status={o.payment_status} /></td>
                  <td><StatusPill status={o.status} /></td>
                  <td className="adm-mono" style={{ color: "#8B8B93" }}>{fmtDate(o.created_at)}</td>
                  <td className="adm-mono" style={{ textAlign: "right" }}>{formatINR(o.total)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {order && (
        <>
          <div className="adm-drawer-scrim" onClick={() => { setOpenId(null); setParams({}); }} />
          <div className="adm-drawer">
            <div className="adm-drawer-h">
              <div>
                <div className="adm-card-title adm-mono">{order.id}</div>
                <div className="adm-card-sub">{fmtDate(order.created_at)}</div>
              </div>
              <button className="adm-icon-btn" onClick={() => { setOpenId(null); setParams({}); }}><X size={15} /></button>
            </div>
            <div className="adm-drawer-b">
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <StatusPill status={order.status} />
                <StatusPill status={order.payment_status} />
              </div>
              <div style={{ marginBottom: 18 }}>
                <div className="adm-label">Customer</div>
                <div style={{ fontSize: 13.5 }}>{order.customer_name}</div>
                <div className="adm-mono" style={{ fontSize: 12, color: "#8B8B93" }}>{order.customer_email}</div>
              </div>
              <div style={{ marginBottom: 18 }}>
                <div className="adm-label">Items</div>
                <table className="adm-table">
                  <tbody>
                    {(order.line_items || []).map((li, i) => (
                      <tr key={`${li.product_id}-${li.size}-${i}`}>
                        <td>{li.name}<div className="adm-cell-name-sub adm-mono">SIZE {li.size} / x{li.qty} / {li.sku}</div></td>
                        <td className="adm-mono" style={{ textAlign: "right" }}>{formatINR((li.price_inr || 0) * li.qty)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="adm-mono" style={{ fontSize: 13, display: "grid", gap: 6, marginBottom: 18 }}>
                <Row k="Subtotal" v={formatINR(order.subtotal)} />
                <Row k="GST" v={formatINR(order.tax)} />
                <Row k="Shipping" v={order.shipping === 0 ? "FREE" : formatINR(order.shipping)} />
                <Row k="Total" v={formatINR(order.total)} bold />
              </div>
              <div style={{ marginBottom: 18 }}>
                <div className="adm-label">Shipping to</div>
                <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                  {order.shipping_address?.address}{order.shipping_address?.apartment ? `, ${order.shipping_address.apartment}` : ""}<br />
                  {order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.pin}<br />
                  {order.shipping_address?.country || "India"} / <span className="adm-mono">{order.shipping_address?.phone}</span>
                </div>
              </div>
              {order.shipping_meta?.awb ? (
                <div style={{ marginBottom: 12, padding: 14, background: "#1B1B1F", border: "1px solid #2A2A2E", borderRadius: 12 }}>
                  <div className="adm-label" style={{ marginBottom: 8 }}>Shipment</div>
                  <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                    <div><span style={{ color: "#8B8B93" }}>Courier:</span> <strong>{order.shipping_meta.courier_name || "-"}</strong></div>
                    <div><span style={{ color: "#8B8B93" }}>AWB:</span> <span className="adm-mono">{order.shipping_meta.awb}</span></div>
                    {order.shipping_meta.eta && <div><span style={{ color: "#8B8B93" }}>ETA:</span> {order.shipping_meta.eta}</div>}
                    {order.shipping_meta.current_status && <div><span style={{ color: "#8B8B93" }}>Status:</span> {order.shipping_meta.current_status}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                    {order.shipping_meta.label_url ? (
                      <>
                        <a
                          data-testid="open-label-pdf"
                          className="adm-btn"
                          href={order.shipping_meta.label_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 6 2 18 2 18 9"/>
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                            <rect x="6" y="14" width="12" height="8"/>
                          </svg>
                          Print label
                        </a>
                        <button
                          data-testid="refresh-label"
                          className="adm-btn adm-btn-ghost"
                          onClick={fetchLabel}
                          disabled={labelLoading}
                          title="Re-fetch the latest label PDF from Shiprocket"
                        >
                          {labelLoading ? "..." : "Refresh"}
                        </button>
                      </>
                    ) : (
                      <button
                        data-testid="generate-label"
                        className="adm-btn"
                        onClick={fetchLabel}
                        disabled={labelLoading}
                      >
                        {labelLoading ? "Generating..." : "Generate label"}
                      </button>
                    )}
                    {order.shipping_meta.track_url && (
                      <a
                        className="adm-btn adm-btn-ghost"
                        href={order.shipping_meta.track_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Track on courier
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                order.payment_status === "paid" && !["cancelled", "delivered"].includes(order.status) && (
                  <div style={{ marginBottom: 12, padding: 14, background: "#1B1B1F", border: "1px dashed #2A2A2E", borderRadius: 12 }}>
                    <div className="adm-label" style={{ marginBottom: 6 }}>Shipment</div>
                    <div style={{ fontSize: 12.5, color: "#8B8B93", lineHeight: 1.6 }}>
                      Not yet shipped. Click <strong style={{ color: "#ECECEE" }}>Ship via Shiprocket</strong> below to push this order, assign an AWB, and schedule pickup.
                    </div>
                  </div>
                )
              )}
              <div className="adm-card-sub" style={{ marginTop: 14 }}>
                Refunds: handled from Razorpay dashboard. Cancelling here marks the order cancelled in the DB.
              </div>
            </div>
            <div className="adm-drawer-f">
              {order.payment_status === "paid" && !order.shipping_meta?.awb && !["cancelled", "delivered"].includes(order.status) && (
                <button
                  data-testid="ship-via-shiprocket"
                  className="adm-btn"
                  onClick={shipViaShiprocket}
                  disabled={shipping}
                >
                  {shipping ? "Pushing..." : "Ship via Shiprocket"}
                </button>
              )}
              {order.status === "pending" && <button className="adm-btn adm-btn-ghost" onClick={() => advance("processing")}>Mark processing</button>}
              {order.status === "shipped" && <button className="adm-btn adm-btn-ghost" onClick={() => advance("delivered")}>Mark delivered</button>}
              {!["cancelled", "delivered"].includes(order.status) && (
                <button className="adm-btn adm-btn-danger" onClick={() => setConfirmCancel(true)}>Cancel order</button>
              )}
            </div>
          </div>
        </>
      )}

      {confirmCancel && (
        <div className="adm-modal-scrim" onClick={() => setConfirmCancel(false)}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Cancel this order?</h2>
            <p style={{ color: "#B6B6BD", fontSize: 13, marginTop: 8 }}>The order will be marked cancelled. Refund (if needed) must be issued from the Razorpay dashboard.</p>
            <div style={{ display: "flex", gap: 8, marginTop: 18, justifyContent: "flex-end" }}>
              <button className="adm-btn adm-btn-ghost" onClick={() => setConfirmCancel(false)}>Keep</button>
              <button className="adm-btn adm-btn-danger" onClick={async () => { setConfirmCancel(false); await advance("cancelled"); }}>Cancel order</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const Row = ({ k, v, bold }) => (
  <div style={{ display: "flex", justifyContent: "space-between", fontWeight: bold ? 600 : 400, color: bold ? "#ECECEE" : "#B6B6BD" }}>
    <span>{k}</span><span>{v}</span>
  </div>
);
